// external
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

// interstate components
import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';

// constants
import { BreakPoint } from '@makemydeal/dr-activities-common';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { PROTECTION_PRODUCTS } from '../../../constants';

// utils
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import {
    AcquisitionFeeAmountField,
    AdjustedResidualField,
    BalloonPaymentField,
    BalloonTermTypeField,
    BaseResidualField,
    CashDownField,
    CashDownWithDeferment,
    CashFields,
    CreditDecisionLenderNameField,
    CustomMileageField,
    CustomMileageFieldNumeric,
    DaysToFirstPaymentField,
    FinanceTermField,
    FlexBuyPercentSelectInput,
    FlexBuyTermSelectInput,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    GridTable,
    LeaseTermField,
    LockWarning,
    MaxResidualizedMsrpField,
    MilesPerYearField,
    NetCashPayoutField,
    PaymentFrequency,
    PaymentLoading,
    ProgramField,
    RetailPriceField,
    SecurityDepositField,
    SellingPriceWithDealerDiscounts,
    SellRateField,
    SummaryDueAtSigning,
    LEASE_PAYMENT_GRID_PROGRAM_DISCLAIMER
} from '@makemydeal/dr-dash-components';
import { LoadingSectionParent } from '@makemydeal/dr-shared-components';

// selectors
import * as accessoriesSelectors from '@makemydeal/dr-dash-store/selectors/accessoriesSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { getIsContractingContext } from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { refreshContractValidationErrors } from '@makemydeal/dr-dash-store/actions/contractingActionCreators';

// custom react components
import { AlertError } from '@makemydeal/dr-dash-components';
import { BaseDealSlideOutProvider } from '../../baseDealSlideouts/BaseDealSlideOutProvider';
import OutsideLienholder from '../OutsideLienholder/OutsideLienholder.interstate';
import AutoSelectProgramScoped from '../../AutoSelectProgramScoped';
import { AutoSelectedLenderProgramBanner } from '../../AutoSelectedLenderProgramBanner';
import { AdditionalFields } from './AdditionalFields';
import { useFirstPaymentDateField } from './hooks/useFirstPaymentDateField';
import { CashDownVariant, useCashDownVariant } from './hooks/useCashDownVariant';
import { AnnualMilesVariant, useAnnualMilesVariant } from './hooks/useAnnualMilesVariant';
import { useSummaryFieldLinks } from './hooks/useSummaryFieldLinks';
import { useSummaryBanners } from './hooks/useSummaryBanners';
import ReadOnlyInput from './ReadOnlyInputs.interstate';
import { SummaryGrid } from './SummaryGrid';
import Payoff from './TradeInFields/Payoff.interstate';
import TradeInAllowance from './TradeInFields/TradeAllowance.interstate';
import { DealTypeDropdown } from '../../dealTypeDropdown/DealTypeDropdown';
import { CashFallbackNotice } from '../../dealTypeDropdown/CashFallbackNotice';
import MathStackVarianceWarning from '../../dealSummary/MathStackVarianceWarning.interstate';

// types
import type { StateTree } from '@makemydeal/dr-dash-types';
import { BaseDealBreakPoints } from '../../../utils/useBaseDealBreakpoint';

// CSS styles
import { CompactFeesTaxesContainer, CompactFeesTaxesItem, CompactSummaryDivider } from './BaseDealSummary.style';

const BaseDealSummaryContent = () => {
    const dispatch = useDispatch();

    //incentives
    const totalRebates = useSelector(offerReduxSelectors.getAppliedIncentivesTotalForDisplay);
    const incentives = formatNumberAndCents(totalRebates);

    //accessories
    const totalAccessoriesAmount = useSelector(accessoriesSelectors.getAccessoriesTotalAmount);
    const accessories = formatNumberAndCents(totalAccessoriesAmount);

    //VPP
    const { totalProductPrice } = useSelector(vehicleProtectionSelectors.getVppSummary);
    const vpp = formatNumberAndCents(totalProductPrice);

    //fees
    const totalFees = useSelector(offerReduxSelectors.getTotalFee);
    const totalFeesFormatted = formatNumberAndCents(totalFees);

    // total tax
    const taxTotal = useSelector(offerReduxSelectors.getTotalTax);
    const formattedTotalTax = formatNumberAndCents(taxTotal);

    // current offer type
    const currentOfferType: OfferType = useSelector(offerReduxSelectors.getCurrentOfferType);

    // paymentGrid
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isBaseDealExperience = useSelector(dealerSelectors.isBaseDealExperience);
    const enableOnePayLease_F231960 = useSelector(featureToggleSelectors.enableOnePayLease_F231960);
    const paymentGridEnabledToggle = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const isDealCentralWithFnI = useSelector(dealerSelectors.isDealCentralWithFnI);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);
    const isBalloonDealTypeEnabled = useSelector(dealerSelectors.isBalloonDealTypeEnabledForDealer);
    const isContractingContext = useSelector(getIsContractingContext);

    const isMobile = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` }) || isDealCentralMultiScenario;
    const isLargeScreen = useMediaQuery({ query: `(min-width: ${BaseDealBreakPoints.XL})` });
    const isNarrowScreen = useMediaQuery({ query: '(max-width: 1220px)' });
    const isFniModeActive = isDealCentralWithFnI && isFnIDealMode;

    const isPaymentGridEnabled =
        (isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabledToggle) && isDealCentralExperience && !isFniModeActive;

    // Turn off payment grid when entering FnI mode
    useEffect(() => {
        if (isFniModeActive && paymentGridEnabledToggle) {
            dispatch(offerReduxActionCreators.updatePaymentGridToggle(false));
        }
        if (isFniModeActive && isFlexBuyPayment && flexBuyPaymentGridEnabled) {
            dispatch(offerReduxActionCreators.updateFlexBuyPaymentGridToggle(false));
        }
    }, [isFniModeActive, paymentGridEnabledToggle, flexBuyPaymentGridEnabled, isFlexBuyPayment, dispatch]);

    //due at signing
    const dueAtSigning = useSelector(compositeSelectors.getTotalAmountDue);
    const dueAtSigningEnhancements_E40674 = useSelector(featureToggleSelectors.enableDueAtSigningEnhancements);

    // net cash payout - only show field when value > 0 to avoid empty grid slots
    const netCashPayoutValue = useSelector(offerReduxSelectors.getNetCashPayout);

    const { showsLeasePaymentGridDisclaimer, showsPaymentGridError, showsAccessoryPricesAdjusted, showsAutoSelectedLenderProgram } =
        useSummaryBanners();

    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);

    const shouldShowAutoSelectProgramScoped = useMemo(() => {
        if (isExplicitLenderP1Enabled) {
            return false;
        }
        if (isFlexBuyPayment || currentOfferType === CASH) {
            return false;
        } else if (isDealCentralWithFnI) {
            return !isFnIDealMode;
        } else if (isDealCentralExperience) {
            return true;
        }
        return false;
    }, [
        isExplicitLenderP1Enabled,
        isFlexBuyPayment,
        isDealCentralWithFnI,
        isFnIDealMode,
        isDealCentralExperience,
        currentOfferType
    ]);

    const { label: firstPaymentDateLabel, value: firstPaymentDateValue } = useFirstPaymentDateField();

    const shouldApplyFlexiWrapper = !(isFlexBuyPayment && (!isDealCentralExperience || isFniModeActive) && isLargeScreen);

    const paymentGridInputSpan = isPaymentGridEnabled ? 2 : 1;

    const annualMilesVariant = useAnnualMilesVariant();
    const MilesPerYearComponent = {
        [AnnualMilesVariant.MILES_PER_YEAR]: <MilesPerYearField isSummaryTab />,
        [AnnualMilesVariant.CUSTOM_MILEAGE]: <CustomMileageField />,
        [AnnualMilesVariant.CUSTOM_MILEAGE_NUMERIC]: <CustomMileageFieldNumeric />
    }[annualMilesVariant];

    // Field definitions for lease section
    const baseResidualFieldConfig = {
        condition: (type: OfferType) => type === LEASE,
        component: <BaseResidualField tab={DealTabs.SUMMARY} />,
        tabletSpan: paymentGridInputSpan,
        desktopSpan: paymentGridInputSpan
    };

    const milesPerYearFieldConfig = {
        condition: (type: OfferType) => type === LEASE,
        component: MilesPerYearComponent
    };

    const cashDownVariant = useCashDownVariant();
    const { openLenderFromRate, openIncentivesFromProgram } = useSummaryFieldLinks();
    const cashDownComponent = {
        [CashDownVariant.CASH_RECEIVED]: <CashFields variant="summary" />,
        [CashDownVariant.DEFERMENT]: <CashDownWithDeferment />,
        [CashDownVariant.CASH_DOWN]: <CashDownField />
    }[cashDownVariant];

    const leftSectionFields = [
        { component: <RetailPriceField /> },
        { component: <SellingPriceWithDealerDiscounts /> },
        {
            condition: (type: OfferType) => type !== CASH,
            component: cashDownComponent
        },
        ...(dueAtSigningEnhancements_E40674
            ? [
                  {
                      condition: (type: OfferType) => type === LEASE,
                      component: <SummaryDueAtSigning inputValue={String(dueAtSigning)} />
                  }
              ]
            : []),
        { component: <TradeInAllowance /> },
        { component: <Payoff /> },
        {
            condition: (type: OfferType) => type === CASH,
            component: <CashDownField />
        },
        ...(netCashPayoutValue > 0 ? [{ component: <NetCashPayoutField /> }] : []),
        { condition: (type: OfferType) => type === LEASE, component: <SecurityDepositField tab={DealTabs.SUMMARY} /> },
        milesPerYearFieldConfig,
        {
            condition: (type: OfferType) => type === LEASE,
            component: <AcquisitionFeeAmountField tab={DealTabs.SUMMARY} />,
            tabletSpan: paymentGridInputSpan,
            desktopSpan: paymentGridInputSpan
        },
        {
            condition: (type: OfferType) => type === LEASE,
            component: <AdjustedResidualField tab={DealTabs.SUMMARY} />
        },
        baseResidualFieldConfig,
        {
            condition: (type: OfferType) => type === LEASE,
            component: <MaxResidualizedMsrpField />,
            tabletSpan: paymentGridInputSpan,
            desktopSpan: paymentGridInputSpan
        },
        {
            component: (
                <CompactFeesTaxesContainer>
                    <CompactFeesTaxesItem>
                        <ReadOnlyInput label="Fees" inputValue={totalFeesFormatted} tab={DealTabs.FEES} />
                    </CompactFeesTaxesItem>
                    <CompactFeesTaxesItem>
                        <ReadOnlyInput label="Taxes" inputValue={formattedTotalTax} tab={DealTabs.TAXES} />
                    </CompactFeesTaxesItem>
                </CompactFeesTaxesContainer>
            )
        },

        {
            condition: (type: OfferType) => type === BALLOON && isBalloonDealTypeEnabled,
            component: <BalloonPaymentField />
        },
        {
            condition: (type: OfferType) => type === BALLOON && isBalloonDealTypeEnabled,
            component: <BalloonTermTypeField />
        }
    ];

    const rightSectionFields = [
        {
            condition: (type: OfferType) => type !== CASH,
            component: (
                <CreditDecisionLenderNameField
                    isSummaryTab={true}
                    onLenderSlideOutOpen={() => {
                        dispatch(interactionAnalyticsActionCreators.mvLenderLink());
                        dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Lender));
                    }}
                    onChangeContractFieldValidation={(lenderName) => {
                        if (isContractingContext && lenderName) {
                            dispatch(refreshContractValidationErrors());
                        }
                    }}
                />
            ),
            mobileSpan: 1,
            ...(currentOfferType === LEASE && { desktopSpan: 2 }),
            ...(currentOfferType !== LEASE && { fieldSpan: 2 }),
            isFlexWrapper: shouldApplyFlexiWrapper
        },
        ...(!isFniModeActive && !isBaseDealExperience && !isFlexBuyPayment
            ? [
                  {
                      condition: (type: OfferType) => type !== CASH,
                      component: <ProgramField onProgramSlideOutOpen={openIncentivesFromProgram} />,
                      mobileSpan: 1,
                      ...(currentOfferType === LEASE && { desktopSpan: 2 }),
                      ...(currentOfferType !== LEASE && { fieldSpan: 2 }),
                      isFlexWrapper: shouldApplyFlexiWrapper
                  }
              ]
            : []),

        { condition: (type: OfferType) => type === LEASE && !isFlexBuyPayment, component: <LeaseTermField isSummaryTab={true} /> },
        {
            condition: (type: OfferType) => type === LEASE && enableOnePayLease_F231960,
            component: <PaymentFrequency />
        },
        {
            condition: (type: OfferType) => (type === FINANCE || type === BALLOON) && !isFlexBuyPayment,
            component: <FinanceTermField isSummaryTab={true} />
        },
        {
            condition: () => isFlexBuyPayment,
            component: <FlexBuyTermSelectInput tab={DealTabs.SUMMARY} />
        },
        {
            condition: () => isFlexBuyPayment,
            component: <FlexBuyPercentSelectInput tab={DealTabs.SUMMARY} />
        },
        {
            condition: (type: OfferType) => type !== CASH,
            component: <SellRateField tabName={DealTabs.SUMMARY} onLenderSlideOutOpen={openLenderFromRate} />
        },
        { condition: (type: OfferType) => type === FINANCE || type === BALLOON, component: <DaysToFirstPaymentField /> },
        {
            condition: (type: OfferType) => type === FINANCE || type === BALLOON,
            component: (
                <ReadOnlyInput
                    label={firstPaymentDateLabel}
                    data-testId="summary-tab-first-payment-date"
                    inputValue={firstPaymentDateValue}
                    type="text"
                />
            )
        },
        {
            component: <ReadOnlyInput label="Incentives" inputValue={incentives} tab={DealTabs.INCENTIVES} />,
            isFlexWrapper: shouldApplyFlexiWrapper
        },
        {
            component: (
                <ReadOnlyInput
                    label={PROTECTION_PRODUCTS}
                    data-testId="Protection Products"
                    inputValue={vpp}
                    tab={DealTabs.PROTECTION}
                />
            ),
            isFlexWrapper: shouldApplyFlexiWrapper
        },
        {
            component: <ReadOnlyInput label="Accessories" inputValue={accessories} tab={DealTabs.ACCESSORIES} />,
            isFlexWrapper: shouldApplyFlexiWrapper
        }
    ];

    // TODO: Barys Marukhin, refactor Scenario Alerts from below, move each alert in a separate file
    return (
        <LoadingSectionParent>
            <PaymentLoading testId="loading-indicator" />

            <Grid padding={'0px'} sx={{ containerName: 'base-deal-summary-container', containerType: 'inline-size' }}>
                <MathStackVarianceWarning />
                <CashFallbackNotice />
                {isDealCentralExperience && (
                    <Box>
                        {showsLeasePaymentGridDisclaimer && (
                            <Grid xs={12} padding="0" marginBottom="1rem">
                                <Alert role="banner" type="info" data-testid="program-alert-warning">
                                    <Typography variant="label-md">{LEASE_PAYMENT_GRID_PROGRAM_DISCLAIMER}</Typography>
                                </Alert>
                            </Grid>
                        )}
                        {showsPaymentGridError && <AlertError message={GENERIC_LAST_FIELD_CHANGED_ERROR} />}

                        <LockWarning />
                    </Box>
                )}
                {showsAccessoryPricesAdjusted && (
                    <Grid xs={12} padding="0" marginBottom="1rem">
                        <Alert role="banner" type="info" data-testid="accessory-price-adjusted-alert">
                            <Typography variant="label-md">We adjusted some accessory prices.</Typography>
                            <Typography variant="label-md" sx={{ display: 'block', marginTop: (theme) => theme.spacing(0.5) }}>
                                Negative values were changed to{' '}
                                <Typography variant={'label-md'} sx={{ fontWeight: '500' }}>
                                    $0.00
                                </Typography>{' '}
                                so payments can be calculated correctly.
                            </Typography>
                        </Alert>
                    </Grid>
                )}
                {showsAutoSelectedLenderProgram && <AutoSelectedLenderProgramBanner />}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile || (isNarrowScreen && isPaymentGridEnabled) ? 'column' : 'row',
                        gap: '1.714rem',
                        width: '100%'
                    }}
                >
                    {isPaymentGridEnabled && isDealCentralExperience && (isMobile || isNarrowScreen) && !isFniModeActive && (
                        <Box sx={{ width: '100%' }}>
                            <GridTable />
                        </Box>
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '1rem'
                            }}
                        >
                            <Box width="100%">
                                <DealTypeDropdown />
                            </Box>
                            {shouldShowAutoSelectProgramScoped && <AutoSelectProgramScoped />}
                        </Box>
                        <Box sx={{ mt: '10px' }}>
                            <SummaryGrid
                                leftSectionFields={leftSectionFields}
                                rightSectionFields={rightSectionFields}
                                isCompactUI
                            />
                            {currentOfferType === CASH && <OutsideLienholder />}
                        </Box>
                    </Box>
                    {isPaymentGridEnabled && isDealCentralExperience && !isMobile && !isNarrowScreen && !isFniModeActive && (
                        <Box sx={{ flex: 1 }}>
                            <GridTable />
                        </Box>
                    )}
                </Box>

                <CompactSummaryDivider />
                <AdditionalFields />
            </Grid>
        </LoadingSectionParent>
    );
};

// eslint-disable-next-line react/no-multi-comp -- BaseDealSummaryContent must be separate to use useBaseDealSlideOut hook within BaseDealSlideOutProvider context
const BaseDealSummary = ({ scopeId }: { scopeId?: string }) => {
    return (
        <BaseDealSlideOutProvider scopeId={scopeId}>
            <BaseDealSummaryContent />
        </BaseDealSlideOutProvider>
    );
};

export default BaseDealSummary;
